/* eslint-disable react/jsx-max-depth */
import { useEffect, useMemo, useState } from "react";

import type { AnyObject, ColumnsType } from "@similarweb/pro-logic-components";
import { Table } from "@similarweb/pro-logic-components";
import { Flex } from "@similarweb/ui-components/foundations";

import { useSubtitleFilters } from "app/custom-hooks/useSubtitleFilters";
import { translate } from "app/filters/i18nFilter";
import { switcherToDataIndexMap } from "app/pages/digital-marketing/ad-intelligence/common/shared/hooks/useSwitcherLogic";
import {
    ShareTypeSwitcher,
    SwitcherOption,
} from "app/pages/digital-marketing/ad-intelligence/common/shared/ShareTypeSwitcher";
import { WidgetBoxWithMarginBottom } from "app/pages/digital-marketing/ad-intelligence/common/StyledComponents";
import { getNoDataObject, PAGE_SIZE_SMALL } from "app/pages/digital-marketing/ad-intelligence/common/utils/utils";

import { getTopIndustriesColumns } from "./getTopIndustriesColumns";
import { getTopIndustriesCompareColumns } from "./getTopIndustriesCompareColumns";
import { TOP_INDUSTRIES_PAGE_SIZE } from "./topIndustriesMetric";
import { useTopIndustries } from "./useTopIndustries";
import { Badge, Label } from "@similarweb/ui-components";

export const TopIndustriesTable = () => {
    const subtitleFilters = useSubtitleFilters("date", "country", "webSource");
    const [selectedSwitcherOption, setSelectedSwitcherOption] = useState<SwitcherOption>(SwitcherOption.Visits);
    const [currentPage, setCurrentPage] = useState(1);

    const { data, isLoading, isError, isCompareMode, domains } = useTopIndustries();

    useEffect(() => {
        setCurrentPage(1);
    }, [selectedSwitcherOption, isCompareMode]);

    const columns = useMemo(
        () =>
            (isCompareMode
                ? getTopIndustriesCompareColumns(selectedSwitcherOption, domains, currentPage)
                : getTopIndustriesColumns(selectedSwitcherOption, currentPage)) as ColumnsType<AnyObject>,
        [isCompareMode, selectedSwitcherOption, domains, currentPage],
    );

    const noDataObject = getNoDataObject(isError, "publisher");

    return (
        <WidgetBoxWithMarginBottom data-automation="top-industries-table">
            <Table
                key={`${isCompareMode}-${selectedSwitcherOption}`}
                theme="white"
                bordersConfig={{ body: false, header: false }}
                columnConfig={columns}
                data={data ?? []}
                isLoading={isLoading}
                loaderRowCount={PAGE_SIZE_SMALL}
                noData={noDataObject}
                pagination={{
                    current: currentPage,
                    pageSize: TOP_INDUSTRIES_PAGE_SIZE,
                    onChange: (_current, _pageSize, next) => setCurrentPage(next),
                }}
            >
                <Table.TableTop withoutRowBorders>
                    <Table.TableTop.Title
                        // @ts-expect-error: title accepts react node not only a string
                        title={
                            <Flex
                                alignItems="center"
                                gap="8px"
                            >
                                {translate("ad.intelligence.publisher.overview.top.industries.table.title")}{" "}
                                <Badge type="beta" />
                            </Flex>
                        }
                        tooltip={translate("ad.intelligence.publisher.overview.top.industries.table.tooltip", {
                            sortBy: switcherToDataIndexMap[selectedSwitcherOption],
                        })}
                        filters={subtitleFilters}
                        extensions={[
                            <ShareTypeSwitcher
                                key="ShareTypeSwitcher"
                                selectedSwitcherOption={selectedSwitcherOption}
                                setSelectedSwitcherOption={setSelectedSwitcherOption}
                            />,
                        ]}
                    />
                </Table.TableTop>
            </Table>
        </WidgetBoxWithMarginBottom>
    );
};
